import React, { useRef, useState } from 'react';
import { addMinutes, format } from 'date-fns';

import styles from './sliderButton.module.css';

type Props = {
  min: number; // in minutes, e.g., 0 for 03:00
  max: number; // in minutes, e.g., 1439 for 02:00 next day
  value: number;
  onChange: (value: number) => void;
  trackWidth: number;
};

function formatTime(minutes: number) {
  // 0 = 03:00, 60 = 04:00, ..., 1380 = 02:00 next day
  const offsetMinutes = (Math.round(minutes) + 180) % 1440;
  const base = new Date(2000, 0, 1, 0, 0);
  const date = addMinutes(base, offsetMinutes);
  return format(date, 'HH:mm');
}
export const SliderButton: React.FC<Props> = ({ min, max, value, onChange, trackWidth }) => {
  const [dragging, setDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  const getPosition = (clientX: number) => {
    if (!sliderRef.current) return min;
    const rect = sliderRef.current.getBoundingClientRect();
    let x = clientX - rect.left;
    x = Math.max(0, Math.min(x, trackWidth));
    const percent = x / trackWidth;
    return min + percent * (max - min);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragging(true);
    e.preventDefault();
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!dragging) return;
    const newValue = getPosition(e.clientX);
    onChange(newValue);
  };

  const handleMouseUp = () => setDragging(false);

  React.useEffect(() => {
    if (dragging) {
      globalThis.addEventListener('mousemove', handleMouseMove);
      globalThis.addEventListener('mouseup', handleMouseUp);
      return () => {
        globalThis.removeEventListener('mousemove', handleMouseMove);
        globalThis.removeEventListener('mouseup', handleMouseUp);
      };
    }
  });

  // Calculate left position
  const percent = (value - min) / (max - min);
  const left = percent * trackWidth;

  return (
    <div ref={sliderRef} className={styles.sliderTrack} style={{ width: trackWidth }}>
      <div
        className={styles.sliderButton}
        style={{ left }}
        data-testid='slider-button'
        onMouseDown={handleMouseDown}
        aria-valuenow={value}
        aria-valuemin={min}
        aria-valuemax={max}
        tabIndex={0}
      >
        <span className={styles.timeLabel}>{formatTime(value)}</span>
      </div>
    </div>
  );
};

export default SliderButton;
